/* eslint-disable prefer-arrow-callback */
import { useCallback, useRef } from "react";

import { productAnalytics } from "utils";

import type { AutosavedChangeType } from "pages/app/scenario-analysis/utils/soilSightTracking";

/** The autosaves see only the payload, so we tag each edit where it happens. We report it when its save lands. */
export const useScenarioUpdateTracking = () => {
  const pendingRef = useRef(new Map<string, Set<AutosavedChangeType>>());

  const markScenarioChange = useCallback(function markScenarioChange(scenarioId: string, changeType: AutosavedChangeType) {
    const pending = pendingRef.current.get(scenarioId) ?? new Set<AutosavedChangeType>();
    pending.add(changeType);
    pendingRef.current.set(scenarioId, pending);
  }, []);

  const reportSaved = useCallback(function reportSaved(scenarioId: string, changeTypes: AutosavedChangeType[]) {
    const pending = pendingRef.current.get(scenarioId);
    changeTypes
      .filter((changeType) => pending?.has(changeType))
      .forEach((changeType) => {
        pending?.delete(changeType);
        productAnalytics.track("soilsights:scenario_update", { change_type: changeType });
      });
  }, []);

  const onScenarioMetaSaved = useCallback(
    function onScenarioMetaSaved(scenarioId: string) {
      reportSaved(scenarioId, ["rename"]);
    },
    [reportSaved]
  );

  const onCropYearsSaved = useCallback(
    function onCropYearsSaved(scenarioId: string) {
      reportSaved(scenarioId, ["rotation", "economics"]);
    },
    [reportSaved]
  );

  return { markScenarioChange, onScenarioMetaSaved, onCropYearsSaved };
};
